/** Models for skill-related data structures. */

export enum SkillVisibility {
  PRIVATE = "private",
  PROJECT = "project",
  PUBLIC = "public",
}

export enum SkillScopeFilter {
  MARKETPLACE = "marketplace",
  PROJECT = "project",
  PROJECT_WITH_MARKETPLACE = "project_with_marketplace",
}

export enum SkillSortBy {
  CREATED_DATE = "created_date",
  ASSISTANTS_COUNT = "assistants_count",
  RELEVANCE = "relevance",
}

export enum SkillCategory {
  DEVELOPMENT = "development",
  ENGINEERING = "engineering",
  TESTING = "testing",
  QUALITY_ASSURANCE = "quality_assurance",
  CODE_REVIEW = "code_review",
  DOCUMENTATION = "documentation",
  DEVOPS = "devops",
  SECURITY = "security",
  COMPLIANCE = "compliance",
  MONITORING_ALERTS = "monitoring_alerts",
  ARCHITECTURE = "architecture",
  UI_UX_DESIGN = "ui_ux_design",
  DATA_ANALYSIS = "data_analysis",
  DATA_ANALYTICS = "data_analytics",
  PROJECT_MANAGEMENT = "project_management",
  PRODUCT_MANAGEMENT = "product_management",
  BUSINESS_ANALYSIS = "business_analysis",
  MIGRATION_MODERNIZATION = "migration_modernization",
  SUPPORT = "support",
  CUSTOMER_EXPERIENCE = "customer_experience",
  KNOWLEDGE_MANAGEMENT = "knowledge_management",
  TRAINING = "training",
  PRESALES = "presales",
  INTERVIEW = "interview",
  TALENT_ACQUISITION = "talent_acquisition",
  OTHER = "other",
}

export interface SkillCreatedBy {
  id: string;
  username: string;
  name: string;
}

export interface SkillListItem {
  id: string;
  name: string;
  description: string;
  project: string;
  visibility: SkillVisibility;
  created_by: SkillCreatedBy | null;
  categories: SkillCategory[];
  createdDate: string;
  updatedDate: string | null;
  is_attached: boolean;
  assistants_count: number;
  user_abilities: string[];
  unique_likes_count: number;
  unique_dislikes_count: number;
}

export interface SkillDetail extends SkillListItem {
  display_name?: string | null;
  content: string;
  toolkits: unknown[];
  mcp_servers: unknown[];
}

export interface SkillListPaginatedResponse {
  skills: SkillListItem[];
  page: number;
  perPage: number;
  pages: number;
  total: number;
}

/** Skill category API response item. */
export interface SkillCategoryItem {
  value: string;
  label: string;
  description?: string;
}

/** Parameters for creating a skill. */
export interface SkillCreateParams {
  /** Kebab-case identifier, 3–64 chars (e.g. "my-skill"). Must start and end with a letter or number. */
  name: string;
  /** 10–1000 chars. */
  description: string;
  /** Markdown skill instructions, minimum 100 chars. */
  content: string;
  project: string;
  visibility?: SkillVisibility;
  /** Max 3 categories. */
  categories?: string[];
  toolkits?: unknown[];
  mcp_servers?: unknown[];
}

/** Parameters for updating a skill. */
export interface SkillUpdateParams {
  /** Kebab-case identifier, 3–64 chars. Must start and end with a letter or number. */
  name?: string;
  /** 10–1000 chars. */
  description?: string;
  /** Markdown skill instructions, minimum 100 chars. */
  content?: string;
  project?: string;
  visibility?: SkillVisibility;
  /** Max 3 categories. */
  categories?: string[];
  toolkits?: unknown[];
  mcp_servers?: unknown[];
}

/** Parameters for importing a skill from .md file. */
export interface SkillImportParams {
  /**
   * Base64-encoded markdown file content. The file must include YAML frontmatter
   * with `name` and `description` fields. This matches the format produced by `export()`.
   *
   * @example
   * Buffer.from("---\nname: my-skill\ndescription: What this skill does\n---\n\n# Instructions\n...").toString("base64")
   */
  file_content: string;
  filename: string;
  project: string;
  visibility?: SkillVisibility;
}